import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, LifeBuoy, Plus, Search, X } from 'lucide-react';

import { PageHeader } from '@/components/PageHeader';
import { TicketPriorityBadge, TicketStatusBadge } from '@/components/support/TicketBadges';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Pagination } from '@/components/ui/Pagination';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { Table, type Column } from '@/components/ui/Table';
import { TICKET_CATEGORY_LABEL, TICKET_CATEGORY_OPTIONS, TICKET_STATUS_OPTIONS } from '@/constants/support';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { toast } from '@/hooks/useToast';
import { formatDateTime } from '@/lib/format';
import type { TicketCategory, TicketStatus, TicketSummary } from '@/types/support';
import { supportApi } from '../api/support.api';
import { NewTicketDialog } from '../components/NewTicketDialog';

const PAGE_SIZE = 20;

const STATUS_FILTER = [{ value: '', label: 'Every status' }, ...TICKET_STATUS_OPTIONS];
/** The category chosen when the ticket was raised — the "About" column. */
const CATEGORY_FILTER = [{ value: '', label: 'Every reason' }, ...TICKET_CATEGORY_OPTIONS];

/** The list's filters, as they sit in the address bar. */
type Filters = {
  page: number;
  status: TicketStatus | '';
  category: TicketCategory | '';
  from: string;
  to: string;
  search: string;
};

const DEFAULT_FILTERS: Filters = {
  page: 1,
  status: '',
  category: '',
  from: '',
  to: '',
  search: '',
};

/** What a link may set — the same choices the two dropdowns offer. */
const FILTER_OPTIONS = {
  allowed: {
    status: TICKET_STATUS_OPTIONS.map((option) => option.value),
    category: TICKET_CATEGORY_OPTIONS.map((option) => option.value),
  },
};

/** The table's columns. A function so the View button can open the ticket. */
const columns = (onView: (row: TicketSummary) => void): Column<TicketSummary>[] => [
  {
    key: 'ticket',
    header: 'Ticket',
    render: (row) => (
      <div className="min-w-0">
        <p className="font-mono text-xs text-ink-3">{row.ticketNumber}</p>
        <p className="truncate font-[560] text-ink">{row.subject}</p>
      </div>
    ),
  },
  {
    key: 'category',
    header: 'About',
    hideOnMobile: true,
    render: (row) => <span className="text-ink-2">{TICKET_CATEGORY_LABEL[row.category]}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    render: (row) => (
      <div className="flex flex-wrap items-center gap-1.5">
        <TicketStatusBadge status={row.status} />
        <TicketPriorityBadge priority={row.priority} />
      </div>
    ),
  },
  {
    key: 'raisedBy',
    header: 'Raised by',
    hideOnMobile: true,
    render: (row) => <span className="text-ink-2">{row.raisedBy.name}</span>,
  },
  {
    key: 'activity',
    header: 'Last update',
    hideOnMobile: true,
    render: (row) => <span className="whitespace-nowrap text-ink-2">{formatDateTime(row.lastActivityAt)}</span>,
  },
  {
    key: 'actions',
    header: <span className="sr-only">Actions</span>,
    align: 'right',
    render: (row) => (
      <Button
        variant="secondary"
        size="sm"
        className="text-[#4f46e5] border-[#4f46e5]  "
        leftIcon={<Eye className="h-4 w-4" aria-hidden />}
        aria-label={`View ticket ${row.ticketNumber}`}
        onClick={() => onView(row)}
      >
        View
      </Button>
    ),
  },
];

/**
 * The institute's support tickets — every one raised by anybody here, most
 * recently active first.
 *
 * Always available, whatever features the school has switched on: losing
 * access to something is exactly when a school needs to be able to ask.
 */
export function SupportTicketsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const { page, status, category, from, to, search } = filters;
  // Typed words wait a moment, so a request is not sent per keystroke.
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [isNewOpen, setIsNewOpen] = useState(false);

  const { data, isLoading, error, refetch } = useApiQuery(
    () =>
      supportApi.list({
        page,
        limit: PAGE_SIZE,
        status: status || undefined,
        category: category || undefined,
        from: from || undefined,
        to: to || undefined,
        search: debouncedSearch || undefined,
      }),
    [page, status, category, from, to, debouncedSearch],
  );

  const isFiltered = Boolean(status || category || from || to || debouncedSearch);

  // Any filter change returns to page 1, in the same write as the filter.
  function updateFilters(patch: Partial<Filters>) {
    setFilters({ ...patch, page: patch.page ?? 1 });
  }

  function clearFilters() {
    setFilters(DEFAULT_FILTERS);
  }

  const viewTicket = (row: TicketSummary) => navigate(`/admin/support/${row.uuid}`);

  const newButton = (
    <Button className="font-bold text-base" leftIcon={<Plus className="h-4 w-4 font-bold" />} onClick={() => setIsNewOpen(true)}>
      New ticket
    </Button>
  );

  return (
    <>
      <PageHeader
        eyebrow="Help"
        title="Support"
        description={
          data && data.openCount > 0
            ? `${data.openCount} ticket${data.openCount === 1 ? '' : 's'} still open with our support team.`
            : 'Ask our support team for help. Replies arrive here and by email.'
        }
        actions={newButton}
      />

      <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Input
          label="Search"
          placeholder="Ticket no. or subject"
          leftIcon={<Search className="h-4 w-4" />}
          value={search}
          onChange={(event) => updateFilters({ search: event.target.value })}
        />
        <Select
          label="Reason"
          options={CATEGORY_FILTER}
          value={category}
          onChange={(event) => updateFilters({ category: event.target.value as TicketCategory | '' })}
        />
        <Select
          label="Status"
          options={STATUS_FILTER}
          value={status}
          onChange={(event) => updateFilters({ status: event.target.value as TicketStatus | '' })}
        />
        <DatePicker
          label="Raised from"
          value={from}
          max={to || undefined}
          onChange={(event) => updateFilters({ from: event.target.value })}
        />
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <DatePicker
              label="Raised to"
              value={to}
              min={from || undefined}
              onChange={(event) => updateFilters({ to: event.target.value })}
            />
          </div>
          {isFiltered && (
            <Button
              variant="secondary"
              iconOnly
              aria-label="Clear every filter"
              title="Clear filters"
              onClick={clearFilters}
            >
              <X className="h-4 w-4" aria-hidden />
            </Button>
          )}
        </div>
      </div>

      {error ? (
        <QueryError title="Could not load your tickets" error={error} onRetry={refetch} />
      ) : (
        <div className="card overflow-hidden">
          <Table
            variant="plain"
            columns={columns(viewTicket)}
            rows={data?.items ?? []}
            rowKey={(row) => row.uuid}
            isLoading={isLoading}
            empty={
              <EmptyState
                icon={<LifeBuoy className="h-5 w-5" />}
                title={isFiltered ? 'No tickets match these filters' : 'No support tickets yet'}
                description={
                  isFiltered
                    ? 'Change or clear the filters to see the rest.'
                    : 'If something is not working, or you need help with the software, raise a ticket and our team will reply.'
                }
                action={
                  isFiltered ? (
                    <Button variant="secondary" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  ) : (
                    newButton
                  )
                }
              />
            }
          />
          {data && <Pagination
              meta={data.meta}
              onPageChange={(next) => updateFilters({ page: next })}
              itemLabel="tickets"
            />}
        </div>
      )}

      <NewTicketDialog
        isOpen={isNewOpen}
        onClose={() => setIsNewOpen(false)}
        onCreated={(ticket) => {
          setIsNewOpen(false);
          toast.success(`Ticket ${ticket.ticketNumber} raised`, 'Our support team will reply here and by email.');
          navigate(`/admin/support/${ticket.uuid}`);
        }}
      />
    </>
  );
}
